(function installCcmNativeCrystalViewer() {
  if (customElements.get('ccm-native-crystal-viewer')) return;

  const RELEASE = '20260817-ccm-portrait-front-v12';
  const DEFAULT_SCENE = 'scrt1001-tower-qa';
  const SCENES = Object.freeze({
    'SCRT0601-T': ['scrt0601-tower-qa', '60 mm Tower', '60mm 타워'],
    'SCRT0601-B': ['scrt0601-brick-qa', '60 mm Brick', '60mm 브릭'],
    'SCRT0801-T': ['scrt0801-lbbr0702-park1', '80 mm Tower', '80mm 타워'],
    'SCRT0801-B': ['scrt0801-brick-qa', '80 mm Brick', '80mm 브릭'],
    'SCRT1001-T': ['scrt1001-tower-qa', '100 mm Tower', '100mm 타워'],
    'SCRT1001-B': ['scrt1001-brick-qa', '100 mm Brick', '100mm 브릭'],
    'SCRT1201-T': ['scrt1201-tower-qa', '120 mm Tower', '120mm 타워'],
    'SCRT1201-B': ['scrt1201-brick-qa', '120 mm Brick', '120mm 브릭'],
    'SCRT1501-T': ['scrt1501-lbbr1102-tower', '150 mm Tower', '150mm 타워'],
    'SCRT1501-B': ['scrt1501-lbbr1602-brick', '150 mm Brick', '150mm 브릭'],
    'SCRT1801-T': ['scrt1801-tower-qa', '180 mm Tower', '180mm 타워'],
    'SCRT1801-B': ['scrt1801-brick-qa', '180 mm Brick', '180mm 브릭'],
    'SCRT2001-T': ['scrt2001-tower-qa', '200 mm Tower', '200mm 타워'],
    'SCRT2001-B': ['scrt2001-brick-qa', '200 mm Brick', '200mm 브릭'],
    SCHR0801: ['schr0801-none-heart', '80 mm Heart', '80mm 하트'],
    SCHR1001: ['schr1001-qa', '100 mm Heart', '100mm 하트'],
    SCSQ0601: ['scsq0601-qa', '60 mm Cube', '60mm 큐브'],
    SCSQ0801: ['scsq0801-lbbr0702-cube', '80 mm Cube', '80mm 큐브'],
    SCSQ1001: ['scsq1001-qa', '100 mm Cube', '100mm 큐브'],
    SCPR1301: ['scpr1301-lbbr0702-prestige', '130 mm Prestige', '130mm 프레스티지'],
    KCRT0301: ['kcrt0301-accessory', 'Crystal Keychain', '크리스탈 키체인'],
    NLRT0401: ['nlrt0401-accessory', 'Crystal Necklace', '크리스탈 목걸이'],
    WSRT0501: ['wsrt0501-accessory', 'Crystal Wine Stopper', '크리스탈 와인 스토퍼'],
  });
  const STOREFRONT_SHAPES = Object.freeze([
    ['tower', 'SCRT1001-T'],
    ['brick', 'SCRT1001-B'],
    ['heart', 'SCHR0801'],
    ['cube', 'SCSQ0801'],
    ['prestige', 'SCPR1301'],
  ]);

  function sceneKeyFromSku(rawSku, fallbackOrientation) {
    const sku = String(rawSku || '').toUpperCase().trim();
    const crystalSku = sku.match(/(?:SCRT|SCHR|SCSQ|SCPR|KCRT|NLRT|WSRT)\d{4}/)?.[0];
    if (!crystalSku) return null;
    if (!crystalSku.startsWith('SCRT')) return SCENES[crystalSku] ? crystalSku : null;
    const orientation = /-B(?:-|$)/.test(sku)
      ? 'B'
      : /-T(?:-|$)/.test(sku)
        ? 'T'
        : String(fallbackOrientation || 'tower').toLowerCase() === 'brick' ? 'B' : 'T';
    const key = `${crystalSku}-${orientation}`;
    return SCENES[key] ? key : null;
  }

  function canonicalEnvironment(rawEnvironment) {
    const environment = String(rawEnvironment || '').trim();
    return !environment || environment === 'ccm-homepage'
      ? 'neutral-charcoal'
      : environment;
  }

  class CcmNativeCrystalViewer extends HTMLElement {
    static get observedAttributes() {
      return ['scene', 'environment', 'base-mode', 'renderer-url'];
    }

    constructor() {
      super();
      this.frame = null;
      this.hasStarted = false;
      this.unsubscribeVariant = null;
      this.observer = null;
      this.slowTimer = null;
      this.loaderPlayTimer = null;
      this.handleMessage = this.handleMessage.bind(this);
    }

    connectedCallback() {
      this.shapeButtons = Array.from(this.querySelectorAll('[data-ccm-shape-button]'));
      this.status = this.querySelector('[data-ccm-viewer-status]');
      this.statusLabel = this.querySelector('[data-ccm-viewer-status-label]');
      this.frameHost = this.querySelector('[data-ccm-frame-host]');
      this.loaderVideo = this.querySelector('[data-ccm-viewer-loader-video]');
      this.loaderSource = this.querySelector('[data-ccm-viewer-loader-source]');
      this.retry = this.querySelector('[data-ccm-viewer-retry]');
      this.cta = this.querySelector('[data-ccm-viewer-cta]');
      this.bindControls();
      this.syncShapeSelector();
      this.bindProductVariant();
      window.addEventListener('message', this.handleMessage);

      const initialKey = sceneKeyFromSku(this.dataset.sku, this.dataset.defaultOrientation);
      if (initialKey) this.setAttribute('scene', SCENES[initialKey][0]);

      if (this.dataset.eager === 'true' || !('IntersectionObserver' in window)) {
        this.start();
      } else {
        this.observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.start();
          this.observer?.disconnect();
          this.observer = null;
        }, { rootMargin: '240px 0px' });
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('message', this.handleMessage);
      this.observer?.disconnect();
      this.unsubscribeVariant?.();
      clearTimeout(this.slowTimer);
      this.stopLoaderAnimation();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue || !this.isConnected || !this.hasStarted) return;
      this.renderFrame();
    }

    bindControls() {
      this.shapeButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const definition = SCENES[button.dataset.sku];
          if (!definition) return;
          this.dataset.userSelectedScene = 'true';
          this.setAttribute('scene', definition[0]);
        });
      });
      this.retry?.addEventListener('click', () => this.renderFrame());
      this.cta?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const target = String(this.cta.dataset.target || 'link').replace(/^theme-popup-/, '');
        if (target === 'select-products') {
          window.setTimeout(() => {
            document.querySelectorAll('.theme-select-popup').forEach((popup) => popup.classList.add('active'));
          }, 150);
        } else if (target === 'preview-products') {
          window.setTimeout(() => {
            document.querySelectorAll('.theme-preview-popup').forEach((popup) => popup.classList.add('active'));
          }, 150);
        } else if (this.cta.dataset.url) {
          window.location.assign(this.cta.dataset.url);
        }
        this.dispatchEvent(new CustomEvent('ccm:viewer:cta', { bubbles: true }));
      });
    }

    bindProductVariant() {
      if (this.dataset.productMode !== 'true') return;
      const subscribeToTheme = typeof subscribe === 'function' ? subscribe : null;
      const themeEvents = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
      if (subscribeToTheme && themeEvents?.variantChange) {
        this.unsubscribeVariant = subscribeToTheme(themeEvents.variantChange, ({ data }) => {
          if (this.dataset.sectionId && data?.sectionId && this.dataset.sectionId !== data.sectionId) return;
          this.setSku(data?.variant?.sku);
        });
      }
    }

    setSku(sku) {
      const key = sceneKeyFromSku(sku, this.dataset.defaultOrientation);
      if (!key) return false;
      this.dataset.sku = sku;
      this.removeAttribute('data-user-selected-scene');
      this.setAttribute('scene', SCENES[key][0]);
      return true;
    }

    setSceneId(sceneId) {
      this.dataset.userSelectedScene = 'true';
      this.setAttribute('scene', sceneId);
    }

    start() {
      if (this.hasStarted) return;
      this.hasStarted = true;
      this.renderFrame();
    }

    startLoaderAnimation() {
      if (!this.loaderVideo) return;
      clearTimeout(this.loaderPlayTimer);
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const reduceData = navigator.connection?.saveData === true;
      if (reduceMotion || reduceData) {
        this.loaderVideo.pause();
        return;
      }
      if (this.loaderSource && !this.loaderSource.hasAttribute('src')) {
        this.loaderSource.setAttribute('src', this.loaderSource.dataset.src);
        this.loaderVideo.load();
      }
      try {
        this.loaderVideo.currentTime = 0;
      } catch (_error) {
        // Safari can reject currentTime changes before metadata is available.
      }
      this.loaderPlayTimer = setTimeout(() => {
        if (this.classList.contains('is-ready') || this.classList.contains('is-error')) return;
        const playPromise = this.loaderVideo.play();
        if (playPromise?.catch) playPromise.catch(() => {});
      }, 180);
    }

    stopLoaderAnimation() {
      clearTimeout(this.loaderPlayTimer);
      this.loaderPlayTimer = null;
      this.loaderVideo?.pause();
    }

    setStatusLabel(value) {
      if (this.statusLabel) this.statusLabel.textContent = value;
    }

    renderFrame() {
      if (!this.frameHost) return;
      clearTimeout(this.slowTimer);
      this.classList.remove('is-ready', 'is-error', 'is-slow');
      this.setAttribute('aria-busy', 'true');
      this.retry?.setAttribute('hidden', '');
      this.setStatusLabel(this.dataset.loadingLabel);
      this.startLoaderAnimation();

      const rendererUrl = new URL(this.getAttribute('renderer-url'));
      if (
        rendererUrl.hostname === '3dviewer.crystalclearmemories.com'
        && rendererUrl.pathname === '/catalog-renderer/index.html'
      ) {
        rendererUrl.pathname = `/catalog-renderer/releases/${RELEASE}/renderer/index.html`;
      }
      rendererUrl.searchParams.set('scene', this.getAttribute('scene') || DEFAULT_SCENE);
      rendererUrl.searchParams.set(
        'environment',
        canonicalEnvironment(this.getAttribute('environment')),
      );
      rendererUrl.searchParams.set('base', this.getAttribute('base-mode') || 'none');
      rendererUrl.searchParams.set('chrome', 'storefront');
      // Hold the qualified customer-facing pose. The engraving is transparent,
      // so an automatic 360-degree orbit makes the true rear read as the front
      // when the base controls rotate into view. Customers can still drag.
      rendererUrl.searchParams.delete('autospin');
      rendererUrl.searchParams.set('release', RELEASE);

      const frame = document.createElement('iframe');
      frame.title = this.dataset.frameTitle;
      frame.allow = 'fullscreen';
      frame.loading = 'eager';
      frame.referrerPolicy = 'strict-origin-when-cross-origin';
      frame.src = rendererUrl.href;
      this.frameHost.replaceChildren(frame);
      this.frame = frame;
      this.expectedOrigin = rendererUrl.origin;
      this.syncShapeSelector();
      this.slowTimer = setTimeout(() => {
        if (this.classList.contains('is-ready')) return;
        this.classList.add('is-slow');
        this.setStatusLabel(this.dataset.slowLabel);
      }, 16000);
    }

    syncShapeSelector() {
      const activeScene = this.getAttribute('scene') || DEFAULT_SCENE;
      this.shapeButtons.forEach((button) => {
        const definition = SCENES[button.dataset.sku];
        const active = definition?.[0] === activeScene;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    handleMessage(event) {
      if (!this.frame || event.source !== this.frame.contentWindow || event.origin !== this.expectedOrigin) return;
      const payload = event.data;
      if (payload?.schema !== 'ccm-storefront-viewer-event/v1') return;
      if (payload.type === 'ccm:crystal-viewer:ready') {
        clearTimeout(this.slowTimer);
        this.classList.remove('is-error', 'is-slow');
        this.classList.add('is-ready');
        this.setAttribute('aria-busy', 'false');
        this.stopLoaderAnimation();
        this.setStatusLabel('');
        this.dispatchEvent(new CustomEvent('ccm:viewer:ready', { detail: payload, bubbles: true }));
      } else if (payload.type === 'ccm:crystal-viewer:error') {
        clearTimeout(this.slowTimer);
        this.classList.remove('is-ready', 'is-slow');
        this.classList.add('is-error');
        this.setAttribute('aria-busy', 'false');
        this.stopLoaderAnimation();
        this.setStatusLabel(this.dataset.errorLabel);
        this.retry?.removeAttribute('hidden');
        this.dispatchEvent(new CustomEvent('ccm:viewer:error', { detail: payload, bubbles: true }));
      }
    }
  }

  customElements.define('ccm-native-crystal-viewer', CcmNativeCrystalViewer);
  window.CCM_NATIVE_CRYSTAL_SCENES = SCENES;
  window.CCM_NATIVE_CRYSTAL_SHAPES = STOREFRONT_SHAPES;
})();
